import React, { useEffect, useMemo, useState } from 'react';
import './PhoneAIConfirmCard.css';

// Two-phase confirm card. PHONE proposes a write action and returns
// `pending_action = { tool, args, summary, action_token, expires_in_ms }`.
// The card renders the summary and waits for the human to click Confirm,
// which POSTs the token to /api/phoneai/execute.
//
// Money-touching tools start their summary with "⚠ REAL FUNDS" — when we
// detect that, the card switches to a louder "danger" styling so a casual
// click is much harder.

const humanizeTool = (raw) =>
  String(raw || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PhoneAIConfirmCard = ({ pendingAction, onConfirm, onCancel, running }) => {
  const [secondsLeft, setSecondsLeft] = useState(
    Math.max(0, Math.floor((pendingAction?.expires_in_ms || 0) / 1000))
  );

  // Token TTL countdown. After 0, the server will reject the token anyway,
  // but we surface the expiry locally so the user isn't surprised.
  useEffect(() => {
    if (!pendingAction) return undefined;
    setSecondsLeft(Math.max(0, Math.floor((pendingAction.expires_in_ms || 0) / 1000)));
    const id = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [pendingAction]);

  const isDanger = useMemo(() => {
    const s = String(pendingAction?.summary || '');
    return s.includes('⚠') || /REAL FUNDS/i.test(s);
  }, [pendingAction]);

  if (!pendingAction) return null;

  const expired = secondsLeft <= 0;

  return (
    <div className={`phoneai-confirm ${isDanger ? 'phoneai-confirm--danger' : ''}`}>
      <div className="phoneai-confirm-head">
        <span className="phoneai-confirm-badge">
          {isDanger ? '⚠ Confirm — real funds' : 'Confirm action'}
        </span>
        <span className="phoneai-confirm-tool">{humanizeTool(pendingAction.tool)}</span>
      </div>
      <div className="phoneai-confirm-summary">{pendingAction.summary}</div>
      <details className="phoneai-confirm-details">
        <summary>Show raw arguments</summary>
        <pre className="phoneai-confirm-args">{JSON.stringify(pendingAction.args || {}, null, 2)}</pre>
      </details>
      <div className="phoneai-confirm-meta">
        {expired
          ? 'This confirmation has expired — ask PHONE again to re-issue.'
          : `Expires in ${secondsLeft}s`}
      </div>
      <div className="phoneai-confirm-actions">
        <button
          type="button"
          className="phoneai-confirm-cancel"
          onClick={onCancel}
          disabled={running}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`phoneai-confirm-go ${isDanger ? 'phoneai-confirm-go--danger' : ''}`}
          onClick={() => onConfirm(pendingAction.action_token)}
          disabled={running || expired}
        >
          {running ? 'Running…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default PhoneAIConfirmCard;
